import React, { FC, useState, useContext } from 'react'
import { ApolloQueryResult } from 'apollo-client'

import { useGetIdentifiers } from 'modules/shares/hooks/useGetIdentifiers'
import {
  useGetComponentsStateQuery,
  GetComponentsQuery,
  GetComponentsCountQuery,
} from '@sketch/gql-types'
import { useEventDispatch, useOnEvent } from '@sketch/utils'
import { useFlag } from '@sketch/modules-common'

// This is a valid use case to validate document.componentsState
// eslint-disable-next-line no-restricted-imports
import { DocumentComponentsState } from '@sketch/gql-types/expansive'

declare module '@sketch/utils' {
  export interface EventsMap {
    componentsAreReady?: string
  }
}

interface ComponentsStateBase {
  isProcessing: boolean
  componentsState?: DocumentComponentsState
  documentIdentifier?: string
  refetchComponentsState: () => void
}

interface ComponentsStateLoading extends ComponentsStateBase {
  loading: true
}

interface ComponentsStateReady extends ComponentsStateBase {
  loading: false
}

type ComponentsState = ComponentsStateLoading | ComponentsStateReady

export const ComponentsStateContext = React.createContext<ComponentsState>({
  loading: false,
  isProcessing: false,
  componentsState: undefined,
  documentIdentifier: undefined,
  refetchComponentsState: () => {},
})

export const ComponentsStateProvider: FC = ({ children }) => {
  const { versionShortId, shareIdentifier } = useGetIdentifiers()

  const isComponentsOnDemandOn = useFlag('component-ingest-on-demand')

  // We use a state to inform about the progress of the async process (instead
  // of the `loading` from `processComponents` mutation) because the whole
  // process requires several steps:
  // - `document > components > status` field queried (this triggers the ingestion)
  // - `versionUpdated` subscription is completed
  // - `refetch` is called to get the new `componentsState`
  const [isProcessing, setIsProcessing] = useState(false)

  const { data, refetch, loading } = useGetComponentsStateQuery({
    variables: {
      versionShortId,
      shareIdentifier,
    },
    fetchPolicy: 'network-only',
    skip: !versionShortId || !shareIdentifier || !isComponentsOnDemandOn,
  })

  const document = data?.share?.version?.document
  const documentIdentifier = document?.identifier
  const componentsState = document?.components?.status

  // If componentsState is not PROCESSED, the BE will automatically trigger
  // the ingestion when we query for document > components > status field, so
  // the BE returns PROCESSING in this case, and we should update the UI as
  // "Generating..."
  const isGeneratingComponents = componentsState === 'PROCESSING'

  if (isComponentsOnDemandOn && isGeneratingComponents && !isProcessing) {
    setIsProcessing(true)
  }

  const refetchComponentsState = async () => {
    await refetch()
    setIsProcessing(false)
  }

  const value: ComponentsState = {
    loading: loading,
    isProcessing,
    componentsState,
    documentIdentifier,
    refetchComponentsState,
  }

  // When a new version is updated, we refetch the components status to keep it
  // up to date
  useOnEvent(
    'versionIsUpdated',
    ({
      documentIdentifier: documentIdentifierFromSubscription,
      hasComponentManifest,
    }) => {
      if (!isComponentsOnDemandOn) return

      if (
        hasComponentManifest &&
        documentIdentifier === documentIdentifierFromSubscription
      ) {
        refetchComponentsState()
      }
    }
  )

  return (
    <ComponentsStateContext.Provider value={value}>
      {children}
    </ComponentsStateContext.Provider>
  )
}

export const useComponentsState = () => {
  const { componentsState } = useContext(ComponentsStateContext)

  return componentsState
}

export const useComponentsTrigger = (
  refetch?: () =>
    | Promise<ApolloQueryResult<GetComponentsQuery>>
    | Promise<ApolloQueryResult<GetComponentsCountQuery>>
) => {
  const isComponentsOnDemandOn = useFlag('component-ingest-on-demand')

  const { componentsState, documentIdentifier } = useContext(
    ComponentsStateContext
  )

  // Setup event to be triggered when the components are ready, this is needed
  // when the trigger and the reaction to the completion are done in different places
  const onComponentsProcessed = useEventDispatch('componentsAreReady')

  // This event can be used to check when the ingestion is done.
  // Once is complete, it refetches if the `refetch` function is provided,
  // anyway it sends the event 'componentsAreReady' and refetches the componentsState
  useOnEvent(
    'versionIsUpdated',
    ({
      documentIdentifier: documentIdentifierFromSubscription,
      hasComponentManifest,
    }) => {
      if (!isComponentsOnDemandOn) return

      if (
        hasComponentManifest &&
        documentIdentifier === documentIdentifierFromSubscription
      ) {
        refetch?.()
        onComponentsProcessed(documentIdentifier)
      }
    }
  )

  return componentsState
}
